import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { searchUsers, UserSearchResult } from '../../../api/users';
import styles from './impersonation-search-dialog.module.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (user: UserSearchResult) => void;
}

const ImpersonationSearchDialog = ({ open, onClose, onPick }: Props) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  // Debounced server search (mirrors SearchEvent.tsx).
  useEffect(() => {
    if (term.trim().length === 0) {
      setResults([]);
      return undefined;
    }
    const controller = new AbortController();
    const delay = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchUsers(term.trim(), controller.signal);
        if (!controller.signal.aborted) {
          setResults(data);
        }
      } catch {
        if (!controller.signal.aborted) {
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 500);
    return () => {
      clearTimeout(delay);
      controller.abort();
    };

  }, [term]);

  // The component stays mounted (it lives in ProfileMenu), so reset the search
  // whenever the dialog closes to avoid stale term/results on the next open.
  useEffect(() => {
    if (!open) {
      setTerm('');
      setResults([]);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('impersonation.searchTitle', 'Impersonate a user')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          variant="outlined"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t('impersonation.searchPlaceholder', 'Search by name, email or WWID')}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress color="inherit" size={18} />
              </InputAdornment>
            ) : null,
          }}
        />
        <List className={styles.results}>
          {results.map((user) => (
            <ListItem
              key={user.wwid}
              secondaryAction={
                <Button size="small" variant="text" onClick={() => onPick(user)}>
                  {t('impersonation.impersonateAction', 'Impersonate')}
                </Button>
              }
            >
              <ListItemText
                primary={user.name}
                secondary={
                  <span className={styles.secondary}>
                    {user.wwid} · {user.email} · {user.role}
                  </span>
                }
              />
            </ListItem>
          ))}
          {term.trim().length > 0 && !loading && results.length === 0 && (
            <div className={styles.empty}>{t('impersonation.noResults', 'No users found')}</div>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default ImpersonationSearchDialog;
